<template lang="pug">
div(av-track-seen="social media carousel" :class="[$style.socialMediaCarouselSection, backgroundClass, `social-media-carousel-${blockId}`]")
  .header-row(:class="$style.headerRow")
    h2(:class="$style.heading" v-if="heading") {{ heading }}
    p(v-if="description" class="av-paragraph md:text-center") {{ description }}

  .carousel-row(:class="$style.carouselRow" v-if="carouselItems.length")
    swiper-container.swiper.overflow-hidden(
      ref="swiper"
      :init.prop="false"
      :loop.prop="false"
      :slides-per-view.prop="'auto'"
      :space-between.prop="24"
      :slides-per-group.prop="1"
      :pagination.prop="paginationConfig"
      :navigation.prop="navigationConfig"
    )
      swiper-slide(
        v-for="(item, key) in carouselItems"
        :key="`social-media-item-${key}`"
        :class="$style.carouselItem"
      )
        div
          .av-iframe-container(
            :class="{ 'playing': playingVideo === key }"
          )
            iframe.av-iframe(
              :id="`youtube-player-${blockId}-${key}`"
              :data-video-url="getYouTubeEmbedUrl(item)"
              :title="getTitle(item)"
              allow="autoplay"
              allowfullscreen
            )
            img.av-iframe-overlay-image(
              v-if="(getOverlayImageFromBlock(item)).includes('img.youtube.com')"
              v-show="playingVideo !== key"
              :src="getOverlayImageFromBlock(item)"
              alt=""
              :class="$style.overlayImage"
            )
            Block.av-iframe-overlay-image(
              v-else-if="item && item.innerBlocks[0]"
              v-show="playingVideo !== key"
              :block="item.innerBlocks[0]")
            .av-iframe-gradient(
              v-show="playingVideo !== key"
              :class="{'expanded': toggleItemLongDescriptions[key], 'truncated': getDescription(item).length > DESCRIPTION_CHAR_LIMIT}"
              @click="openLongDescription(item, key)"
            )
              h3.av-iframe-title {{ getTitle(item) }}
              p.av-iframe-text(v-html="getTruncatedDescription(item, key)" )
            button.av-play-button(
              v-show="playingVideo !== key"
              aria-label="Play video"
              data-style="redCircle"
              @click="playVideo(key)"
            )
              svg(width="60" height="60" viewBox="0 0 60 60" fill="none" xmlns="http://www.w3.org/2000/svg")
                rect(width="60" height="60" rx="30" fill="#E93574")
                path(d="M46.842 30L21.5789 44.5857L21.5789 15.4143L46.842 30Z" fill="white")
  .swiper-navigation-container(
    v-show="showNavigation"
    :class="$style.swiperNavigationContainer"
  )
    .swiper-button-prev
    .swiper-pagination
    .swiper-button-next
  <svg v-if="backgroundClass === $style.backgroundBlue" width="838" height="450" viewBox="0 0 838 450" fill="none" xmlns="http://www.w3.org/2000/svg" :class="$style.decoration">
    <rect x="-63.3867" y="-3.07568" width="925.669" height="787.092" rx="16" transform="rotate(12.478 -63.3867 -3.07568)" fill="#FFC907"/>
  </svg>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref, nextTick, type Ref } from "vue"
import { getBlockAttribute } from "~/utils/block"
import "swiper/css"
import "swiper/css/navigation"
import "swiper/css/pagination"
import type { SwiperContainer } from "swiper/element"

const SLIDE_WIDTH = 284
const SLIDE_SPACING = 24
const SWIPER_INIT_DELAY = 500
const NAVIGATION_CHECK_DELAY = 700
const YOUTUBE_THUMBNAIL_QUALITY = 'oar2'
const DESCRIPTION_CHAR_LIMIT = 162

export default defineComponent({
  name: "SocialMediaCarouselSection",
  props: {
    block: {
      type: Object as () => Block,
      required: true
    },
    blockId: {
      type: String,
      default: ""
    }
  },

  setup(props) {
    const swiper: Ref<SwiperContainer | null> = ref(null)
    const playingVideo: Ref<number | null> = ref(null)
    const players: Ref<Map<number, any>> = ref(new Map())
    const origin = ref("")
    const showNavigation = ref(false)

    onMounted(async () => {
      if (import.meta.client) {
        origin.value = window.location.origin

        if (!(window as any).YT) {
          const tag = document.createElement("script")
          tag.src = "https://www.youtube.com/iframe_api"
          const firstScriptTag = document.getElementsByTagName("script")[0]
          firstScriptTag.parentNode?.insertBefore(tag, firstScriptTag)
        }

        nextTick(() => {
          setTimeout(() => {
            swiper.value?.initialize()

            const swiperInstance = (swiper.value as any)?.swiper
            if (swiperInstance) {
              const stopAllVideos = () => {
                playingVideo.value = null

                // Pause and reset all videos
                players.value.forEach((player) => {
                  if (player && typeof player.pauseVideo === "function") {
                    player.pauseVideo()
                    player.seekTo(0)
                  }
                })
              }

              swiperInstance.on("slideChangeTransitionStart", () => {
                stopAllVideos()
                ;(window as any).av?.track("cms-frontend", "social_media_carousel_interacted", {})
              })
            }
          }, SWIPER_INIT_DELAY)
        })
      }
    })

    return { swiper, playingVideo, players, origin, showNavigation }
  },

  data() {
    return {
      paginationConfig: {
        el: `.social-media-carousel-${this.blockId} .swiper-pagination`,
        type: "bullets",
        clickable: true
      },
      navigationConfig: {
        nextEl: `.social-media-carousel-${this.blockId} .swiper-button-next`,
        prevEl: `.social-media-carousel-${this.blockId} .swiper-button-prev`
      },
      toggleItemLongDescriptions: ref([])
    }
  },

  computed: {
    heading(): string {
      const headingBlock = this.block.innerBlocks?.[0]
      if (headingBlock?.type === "CORE_HEADING") {
        const match = headingBlock.innerHtml.match(/>([^<]+)<\//)
        return match?.[1] || ""
      }
      return ""
    },

    description(): string {
      const descriptionBlock = this.block.innerBlocks?.[1]
      if (descriptionBlock?.type === "CORE_PARAGRAPH") {
        const match = descriptionBlock.innerHtml.match(/<p[^>]*>([^<]+)<\/p>/)
        return match?.[1] || ""
      }
      return ""
    },

    carouselItems(): any[] {
      const carousel = this.block.innerBlocks?.[2]
      if (carousel?.attributes) {
        const className = getBlockAttribute(carousel.attributes, "className")
        if (className.includes("av-social-media-carousel")) {
          return carousel.innerBlocks || []
        }
      }
      return []
    },

    backgroundClass(): string {
      const className = getBlockAttribute(this.block.attributes, "className")

      if (className.includes("bg-primary")) {
        return (this as any).$style.backgroundBlue
      }

      return ""
    },
    DESCRIPTION_CHAR_LIMIT(): number {
      return DESCRIPTION_CHAR_LIMIT
    }
  },

  mounted() {
    if (import.meta.client) {
      this.toggleItemLongDescriptions = ref(Array(this.carouselItems.length).fill(false))
      nextTick(() => {
          setTimeout(() => {
            const gradients = document.querySelectorAll('.av-iframe-gradient.truncated');
            gradients.forEach((gradient: HTMLElement) => {
              const computedStyle = window.getComputedStyle(gradient);
              gradient.style.top = computedStyle.top;
            });
          }, SWIPER_INIT_DELAY + 1)
      })
      setTimeout(() => {
        this.checkNavigationVisibility()
      }, NAVIGATION_CHECK_DELAY)

      window.addEventListener("resize", this.checkNavigationVisibility)
    }
  },

  beforeUnmount() {
    if (import.meta.client) {
      window.removeEventListener("resize", this.checkNavigationVisibility)

      this.players.forEach(player => {
        if (player && typeof player.destroy === "function") {
          player.destroy()
        }
      })
      this.players.clear()
    }
  },

  methods: {
    checkNavigationVisibility() {
      if (!this.swiper || !import.meta.client) return

      const swiperElement = this.swiper as any
      const swiperInstance = swiperElement.swiper

      if (swiperInstance) {
        const containerWidth = swiperInstance.width
        const totalSlidesWidth = swiperInstance.slides.length * (SLIDE_WIDTH + SLIDE_SPACING)

        this.showNavigation = totalSlidesWidth > containerWidth
      }
    },

    stopAllVideos(exceptIndex: number | null = null) {
      this.playingVideo = null

      // Pause and reset all videos except the one being played
      this.players.forEach((player, index) => {
        if (index !== exceptIndex && player && typeof player.pauseVideo === "function") {
          player.pauseVideo()
          player.seekTo(0)
        }
      })
    },

    playVideo(index: number) {
      if (!import.meta.client) return

      // Stop all other videos (but not this one if it exists)
      this.stopAllVideos(index)

      // Set this as the playing video
      this.playingVideo = index

      // Track video play event
      const item = this.carouselItems[index]
      const videoTitle = this.getTitle(item)
      const videoUrl = this.getVideoUrl(item)
      ;(window as any).av?.track("cms-frontend", "social_media_carousel_video_play", {
        videoIndex: index,
        videoTitle,
        videoUrl
      })

      // Check if player already exists for this video
      const existingPlayer = this.players.get(index)
      if (existingPlayer && typeof existingPlayer.playVideo === "function") {
        // Reuse existing player that was paused
        existingPlayer.seekTo(0)
        existingPlayer.playVideo()
        return
      }

      // Stop all other videos for new player
      this.stopAllVideos(index)

      // Create new player
      const iframe = document.getElementById(
        `youtube-player-${this.blockId}-${index}`
      ) as HTMLIFrameElement
      if (!iframe) return

      const embedUrl = iframe.getAttribute('data-video-url')
      if (!embedUrl) return

      iframe.src = embedUrl

      const iframeId = `youtube-player-${this.blockId}-${index}`

      const initPlayer = () => {
        const player = new (window as any).YT.Player(iframeId, {
          events: {
            onReady: (event: any) => {
              // Only hide overlay when video is ready to play
              this.playingVideo = index
              event.target.playVideo()
            },
            onStateChange: (event: any) => {
              if (event.data === 2) {
                // Video paused
                const watchedDuration = event.target.getCurrentTime()
                ;(window as any).av?.track("cms-frontend", "social_media_carousel_video_paused", {
                  videoIndex: index,
                  videoTitle,
                  videoUrl,
                  watchedDuration: Math.round(watchedDuration)
                })
              } else if (event.data === 0) {
                // Video completed
                const watchedDuration = event.target.getCurrentTime()
                ;(window as any).av?.track(
                  "cms-frontend",
                  "social_media_carousel_video_completed",
                  {
                    videoIndex: index,
                    videoTitle,
                    videoUrl,
                    watchedDuration: Math.round(watchedDuration)
                  }
                )
                this.resetPlayer(index, event.target)
              }
            }
          }
        })
        this.players.set(index, player)
      }

      setTimeout(() => {
        if ((window as any).YT?.Player) {
          initPlayer()
        } else {
          ;(window as any).onYouTubeIframeAPIReady = initPlayer
        }
      }, 200)
    },

    resetPlayer(index: number, player: any) {
      // Just hide the video by setting playingVideo to null
      // Keep the player alive for potential replay
      this.playingVideo = null
    },

    getVideoUrl(item: any): string {
      const videoBlock = item.innerBlocks?.find(
        (block: any) =>
          block.tagName === "EMBED_YOUTUBE_EMBED_URL" ||
          getBlockAttribute(block.attributes || [], "className").includes("av-video-url")
      )
      if (videoBlock?.innerHtml) {
        const match = videoBlock.innerHtml.match(/>([^<]+)</)
        return match?.[1] || ""
      }
      return ""
    },

    getYouTubeEmbedUrl(item: any): string {
      const videoUrl = this.getVideoUrl(item)
      if (!videoUrl) return ""

      const separator = videoUrl.includes('?') ? '&' : '?'
      return `${videoUrl}${separator}enablejsapi=1&origin=${this.origin}&controls=1&modestbranding=1&rel=0&showinfo=0&playsinline=1`
    },

    getTitle(item: any): string {
      const titleBlock = item.innerBlocks?.find(
        (block: any) => block.type === "CORE_HEADING" && block.tagName === "h3"
      )
      if (titleBlock?.innerHtml) {
        const match = titleBlock.innerHtml.match(/>([^<]+)</)
        return match?.[1] || ""
      }
      return ""
    },

    getDescription(item: any): string {
      const descBlock = item.innerBlocks?.find(
        (block: any) =>
          block.type === "CORE_PARAGRAPH" &&
          getBlockAttribute(block.attributes || [], "className").includes("av-iframe-text")
      )
      if (descBlock?.innerHtml) {
        const match = descBlock.innerHtml.match(/<p[^>]*>([^<]+)<\/p>/)
        return match?.[1] || ""
      }
      return ""
    },
    getTruncatedDescription(item: any, key: any): string {
      if (this.toggleItemLongDescriptions[key]) {
        return this.getDescription(item).length > DESCRIPTION_CHAR_LIMIT ? this.getDescription(item) + '<div class="mt-ds-ms underline">View&nbsp;less</div>' : this.getDescription(item)
      }
      return this.getDescription(item).length > DESCRIPTION_CHAR_LIMIT ? this.getDescription(item).slice(0, DESCRIPTION_CHAR_LIMIT) + '&emsp;<span class="underline">View&nbsp;more</span>' : this.getDescription(item)
    },
    openLongDescription(item, key): void {
      if (this.getDescription(item).length > DESCRIPTION_CHAR_LIMIT) {
        this.toggleItemLongDescriptions[key] = !this.toggleItemLongDescriptions[key]
      }
    },

    extractYouTubeVideoId(url: string): string | null {
      const patterns = [
        /youtube\.com.*[?&]v=([^&]{11})/,
        /youtube\.com\/embed\/([^?]{11})/,
        /youtu\.be\/([^?]{11})/,
        /\/shorts\/([^?]+)/
      ]

      for (const pattern of patterns) {
        const match = url.match(pattern)
        if (match?.[1] && match[1].length === 11) {
          return match[1]
        }
      }

      return null
    },

    getOverlayImageFromBlock(item: any): string {
      const imageBlock = item.innerBlocks?.find(
        (block: any) => block.type === "CORE_IMAGE" ||
                   getBlockAttribute(block.attributes || [], "className").includes("av-thumbnail")
      )
      if (imageBlock?.innerHtml) {
        const match = imageBlock.innerHtml.match(/src="([^"]+)"/)
        if (match?.[1]) {
          return match?.[1]
        }
      }

      const url = this.getVideoUrl(item)
      if (!url) return ""

      const videoId = this.extractYouTubeVideoId(url)
      if (videoId) {
        return `https://img.youtube.com/vi/${videoId}/${YOUTUBE_THUMBNAIL_QUALITY}.jpg`
      }

      return ""
    }
  }
})
</script>

<style>
@import "@anyvan-ui/socialmediacard/SocialMediaCard.css";
</style>

<style lang="css" module>
.decoration {
  display: none;
}

.socialMediaCarouselSection {
  @apply w-full;
  overflow: visible;
  position: relative;
}

.headerRow {
  @apply flex flex-col items-start gap-4 relative;
  z-index: 1;
}

.heading {
  @apply text-2xl;
}

.swiperNavigationContainer {
  @apply flex items-center mt-ds-ml mb-ds-sm justify-center gap-ds-lg;
  width: 100%;
  max-width: 100%;
  margin: 0 auto;

  :global {
    .swiper-button-prev,
    .swiper-button-next {
      @apply relative z-[1] min-h-ds-xl min-w-ds-xl h-auto w-auto cursor-pointer flex items-center justify-center rounded-full text-black border border-solid border-black border-opacity-20 bg-white;
      position: static;
      margin: 0;

      &:after {
        @apply text-base;
      }
    }

    .swiper-pagination.swiper-pagination-clickable.swiper-pagination-bullets.swiper-pagination-horizontal {
      @apply z-[99];
      position: static;
      margin: 0;
      width: auto;
    }

    .swiper-pagination {
      @apply flex gap-ds-sm justify-center w-auto;

      .swiper-pagination-bullet {
        @apply h-ds-md w-ds-md rounded-xl border border-solid border-black border-opacity-20 bg-opacity-10 bg-black;
      }

      .swiper-pagination-bullet-active {
        @apply opacity-100 bg-primary z-[1];
      }
    }
  }
}

.backgroundBlue {
  :global {
    .swiper-pagination-bullet {
      background-color: #ffffff !important;
      border-color: #c9c9c9;
      opacity: 1;
    }

    .swiper-pagination-bullet-active {
      background-color: #41A5DD !important;
    }
  }
}

.carouselRow {
  overflow: visible;
  position: relative;
  z-index: 1;

  :global {
    .swiper {
      @apply flex flex-col items-start relative gap-ds-sm justify-start w-screen mt-ds-lg;
      overflow: hidden;

      .swiper-wrapper {
        @apply h-auto;
        overflow: hidden;
      }

      .swiper-slide {
        @apply h-auto;
      }
    }
  }
}

.carouselItem {
  @apply h-auto;
  width: 284px !important;
  flex-shrink: 0;

  > div {
    width: 284px;
    height: 501px;
  }
}

.overlayImage {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 1;
}

:global {
  .av-social-media-carousel-container {
    & .swiper {
      width: calc(100vw - 16px);

      @media (min-width: 768px) {
        width: calc(100vw - 32px);
      }

      @media (min-width: 992px) {
        width: calc(100vw - 64px);
      }

      @media (min-width: 1200px) {
        width: calc(100vw - 96px);
      }
    }
  }

  .av-social-media-carousel-container.bg-primary {
    position: relative;
    overflow: hidden;

    &::before {
      content: '';
      display: block;
      position: absolute;
      left: -12%;
      bottom: -12%;
      rotate: 14deg;
      background: #FFC907;
      width: 70vw;
      height: 500px;
      border-radius: 16px;

      @media (max-width: 767px) {
        left: -24%;
        width: 140vw;
      }
    }
  }

  .av-iframe-container {
    @apply rounded-lg;
  }

  .av-iframe-gradient {
    z-index: 3 !important;
    transition: top 0.2s ease-in-out, background-color 0.2s ease-in-out;
    top: initial;

    &.expanded {
      top: 0 !important;
      z-index: 100 !important;
      background-color: black;
      padding-bottom: 500px;
    }

    &.truncated {
      pointer-events: auto !important;
      cursor: pointer !important;
    }
  }

  .av-play-button {
    z-index: 99 !important;
  }
}

@screen md {
  .headerRow {
    @apply items-center;
  }

  .heading {
    @apply text-3xl text-center;
  }
}

@screen lg {
  .heading {
    @apply text-4xl;
  }
}
</style>
